import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  Globe,
  Lightbulb,
  Loader2,
  Users,
} from 'lucide-react';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import {
  CREATE_NEWS_TIP_INTERVIEW,
  CURRENT_USER,
  GET_MY_CIRCLES,
  GET_MY_INBOX_INTERVIEWS,
} from '@/api/graphql/queries';
import { SAVE_PERSONAL_DATA } from '@/api/graphql/mutations';
import { useAuth } from '@/lib/auth-provider';
import { getAuthToken } from '@/lib/utils';
import { LANGUAGES } from '@/lib/constants';
import { LightShell, LightMark } from '../light-ui';

interface InboxInterview {
  _id: string;
  proxiId: string;
  proxiCastId?: string;
  circleId?: string;
  status?: string;
  createdAt?: string;
  expiresAt?: string;
  invitationToken?: string;
  interviewType: 'MARKETPLACE' | 'CIRCLE';
  isMarketplace?: boolean;
  marketplaceStatus?: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'COMPLETED' | 'EXPIRED';
  seriesId?: string;
  marketplaceInstructions?: string;
  marketplaceReward?: number;
  inviterId?: string;
  isNewMemberWelcome?: boolean;
  inviter?: {
    _id: string;
    personalData?: { firstName?: string; lastName?: string; email?: string; avatar?: string };
  };
  series?: {
    _id: string;
    seriesTitle?: string;
    seriesSummary?: string;
    defaults?: { episodeImageUrl?: string };
  };
  proxiCast?: {
    _id: string;
    episodeName?: string;
    context?: string;
    interviewEnd?: string;
    status?: string;
    episodeImageUrl?: string;
    seriesId?: string;
  };
  circle?: { _id: string; name?: string; avatarPhoto?: string };
}

interface PaginatedInboxInterviews {
  items: InboxInterview[];
  totalCount: number;
  hasMore: boolean;
}

interface Circle {
  _id: string;
  name?: string;
  avatarPhoto?: string;
  description?: string;
  circleAdminId?: string;
  participants?: Array<{ userId?: string; status?: string; role?: string }>;
}

type ViewState = 'list' | 'circles' | 'submitting' | 'confirmation';

interface InterviewsListScreenProps {
  onSignOut?: () => void;
}

const InterviewsListScreen: React.FC<InterviewsListScreenProps> = ({ onSignOut }) => {
  const { currentUser, logout } = useAuth();
  const [viewState, setViewState] = useState<ViewState>('list');
  const [selectedCircle, setSelectedCircle] = useState<Circle | null>(null);
  const [showInProgress, setShowInProgress] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);

  const { data, loading, error } = useQuery<{ getMyInboxInterviews: PaginatedInboxInterviews }>(
    GET_MY_INBOX_INTERVIEWS,
    {
      variables: { pagination: { limit: 20, offset: 0 } },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [fetchCircles, { data: circlesData, loading: circlesLoading }] = useLazyQuery<{ getMyCircles: Circle[] }>(
    GET_MY_CIRCLES,
    { fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    if (viewState === 'circles') fetchCircles();
  }, [viewState, fetchCircles]);

  const [createNewsTipInterview] = useMutation(CREATE_NEWS_TIP_INTERVIEW);
  const [savePersonalData] = useMutation(SAVE_PERSONAL_DATA);

  const interviews = data?.getMyInboxInterviews?.items ?? [];
  const circles = circlesData?.getMyCircles ?? [];
  const currentLanguage = selectedLanguage || currentUser?.personalData?.language || 'ENG';

  const newInterviews = interviews.filter(i => i.status === 'NEW' || i.status === 'WAITINGFORREADY');
  const inProgressInterviews = interviews.filter(i => i.status === 'INTERVIEWING' || i.status === 'PAUSED');

  const handleLanguageChange = async (languageCode: string) => {
    setSelectedLanguage(languageCode);
    setShowLanguageDropdown(false);
    try {
      await savePersonalData({
        variables: { fields: { language: languageCode } },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
      });
    } catch (err) {
      console.error('Error updating language:', err);
      setSelectedLanguage(null);
    }
  };

  const handleSignOut = async () => {
    await logout();
    onSignOut?.();
  };

  const handleStartInterview = (interview: InboxInterview) => {
    if (interview.invitationToken) {
      const currentToken = getAuthToken();
      if (currentToken) localStorage.setItem(`trainToken_${interview.invitationToken}`, currentToken);
      window.location.href = `/invite/${interview.invitationToken}`;
    }
  };

  const handleNewsTipClick = () => {
    setViewState('circles');
    fetchCircles();
  };

  const handleSelectCircle = async (circle: Circle) => {
    setSelectedCircle(circle);
    setViewState('submitting');
    try {
      const result = await createNewsTipInterview({ variables: { input: { circleId: circle._id } } });
      const response = result.data?.createNewsTipInterview;
      if (response?.success && response?.inviteToken) {
        sessionStorage.setItem(`self_created_${response.inviteToken}`, 'true');
        window.location.href = `/invite/${response.inviteToken}`;
      } else {
        console.error('Failed to create news tip interview:', response?.error);
        alert(response?.error || 'Failed to create news tip interview');
        setViewState('circles');
      }
    } catch (err) {
      console.error('Error creating news tip interview:', err);
      alert('Something went wrong. Please try again.');
      setViewState('circles');
    }
  };

  const handleBackToList = () => { setViewState('list'); setSelectedCircle(null); };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getInterviewImage = (i: InboxInterview): string | undefined =>
    i.proxiCast?.episodeImageUrl || i.series?.defaults?.episodeImageUrl || i.circle?.avatarPhoto || undefined;

  const getInterviewTitle = (i: InboxInterview): string => {
    if (i.proxiCast?.episodeName) return i.proxiCast.episodeName;
    if (i.series?.seriesTitle) return i.series.seriesTitle;
    if (i.circle?.name) return `Interview for ${i.circle.name}`;
    return 'Interview';
  };

  const renderInterviewRow = (interview: InboxInterview) => {
    const image = getInterviewImage(interview);
    const title = getInterviewTitle(interview);
    const circleName = interview.circle?.name;
    return (
      <button
        key={interview._id}
        type="button"
        onClick={() => handleStartInterview(interview)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: 14,
          width: '100%',
          textAlign: 'left',
          border: '1px solid var(--lp-line)',
          borderRadius: '2px 14px 2px 14px',
          background: 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          transition: 'background 180ms ease, border-color 180ms ease, transform 180ms ease',
          fontFamily: 'var(--lp-mono)',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.85)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.55)')}
      >
        <div
          style={{
            width: 56, height: 56,
            borderRadius: '2px 12px 2px 12px',
            overflow: 'hidden',
            background: 'rgba(40,50,40,0.06)',
            flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {image ? (
            <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Users style={{ width: 26, height: 26, color: 'var(--lp-ink-mute)' }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lp-serif" style={{ color: 'var(--lp-ink)', fontSize: 16, fontWeight: 380, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
          {circleName && (
            <div style={{ color: 'var(--lp-ink-soft)', fontSize: 12, marginTop: 2, fontFamily: 'var(--lp-mono)', letterSpacing: '0.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {circleName}
            </div>
          )}
          {interview.expiresAt && (
            <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--lp-ink-mute)', fontSize: 11, fontFamily: 'var(--lp-mono)', letterSpacing: '0.04em' }}>
              <Clock style={{ width: 12, height: 12 }} />
              <span>Expires {formatDate(interview.expiresAt)}</span>
            </div>
          )}
        </div>
        <ExternalLink style={{ width: 18, height: 18, color: 'var(--lp-ink-mute)', flexShrink: 0 }} />
      </button>
    );
  };

  return (
    <LightShell scroll>
      <main
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 'calc(env(safe-area-inset-top, 0px) + 24px) clamp(16px, 4vw, 32px) calc(env(safe-area-inset-bottom, 0px) + 80px)',
        }}
      >
        {/* Header — the card below needs clear air from this row, so keep a
            generous bottom margin (22px read as cramped on phones). */}
        <div style={{ width: '100%', maxWidth: 720, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 36, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <LightMark src={PROXI_BRAIN_LOGO} size={48} />
            <div>
              <span className="lp-section-sub" style={{ display: 'block' }}>Welcome back</span>
              <h1 className="lp-serif" style={{ fontSize: 22, color: 'var(--lp-ink)', margin: 0, fontWeight: 380 }}>
                {currentUser?.personalData?.firstName || 'Hello'}
              </h1>
            </div>
          </div>
          <button type="button" onClick={handleSignOut} className="lp-btn-link">
            Sign out
          </button>
        </div>

        {/* Card */}
        <div
          className="lp-card"
          style={{
            width: '100%',
            maxWidth: 720,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {viewState === 'circles' && (
            <>
              <div style={{ padding: '22px 24px 16px', borderBottom: '1px solid var(--lp-line)' }}>
                {/* Balanced header row mirroring the list view (chip in one
                    side column, title truly centered): a compact Back chip
                    instead of the old full-width link crammed into the card's
                    top-left corner. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <button
                      type="button"
                      onClick={handleBackToList}
                      className="lp-frost-chip"
                      style={{ cursor: 'pointer', padding: '6px 10px' }}
                      aria-label="Back to interviews"
                    >
                      <ArrowLeft style={{ width: 12, height: 12 }} />
                      <span>Back</span>
                    </button>
                  </div>
                  <h2 className="lp-section-heading" style={{ textAlign: 'center', fontSize: 'clamp(20px, 3vw, 26px)', margin: 0 }}>
                    Select a circle.
                  </h2>
                  <div style={{ flex: 1 }} />
                </div>
                <p className="lp-prose" style={{ textAlign: 'center', fontSize: 13, marginTop: 8 }}>
                  Choose which circle to submit your tip or video.
                </p>
              </div>
              <div style={{ padding: '20px 24px 24px', overflowY: 'auto' }}>
                {circlesLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 12 }}>
                    <Loader2 style={{ width: 28, height: 28, color: 'var(--lp-ink-mute)', animation: 'lp-spin 1s linear infinite' }} />
                    <p style={{ color: 'var(--lp-ink-mute)', fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.10em' }}>
                      Loading your circles…
                    </p>
                  </div>
                )}

                {!circlesLoading && circles.length === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 10 }}>
                    <Users style={{ width: 36, height: 36, color: 'var(--lp-ink-mute)' }} />
                    <p className="lp-serif" style={{ color: 'var(--lp-ink)', fontSize: 18 }}>Nothing here yet.</p>
                    <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', maxWidth: 360 }}>
                      You're not a member of any circles yet.
                    </p>
                    <a href="https://www.proxiaistudio.com" target="_blank" rel="noopener noreferrer" className="lp-link" style={{ marginTop: 6, fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.10em' }}>
                      Visit Proxi Studio →
                    </a>
                  </div>
                )}

                {!circlesLoading && circles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {circles.map(circle => (
                      <button
                        key={circle._id}
                        type="button"
                        onClick={() => handleSelectCircle(circle)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: 14,
                          textAlign: 'left',
                          border: '1px solid var(--lp-line)',
                          borderRadius: '2px 14px 2px 14px',
                          background: 'rgba(255,255,255,0.55)',
                          cursor: 'pointer',
                          transition: 'background 180ms ease',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.85)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.55)')}
                      >
                        <div style={{
                          width: 52, height: 52,
                          borderRadius: '2px 12px 2px 12px',
                          overflow: 'hidden',
                          background: 'rgba(40,50,40,0.06)',
                          flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {circle.avatarPhoto ? (
                            <img src={circle.avatarPhoto} alt={circle.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Users style={{ width: 24, height: 24, color: 'var(--lp-ink-mute)' }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="lp-serif" style={{ color: 'var(--lp-ink)', fontSize: 16, fontWeight: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {circle.name || 'Unnamed circle'}
                          </div>
                          {circle.description && (
                            <div style={{ color: 'var(--lp-ink-soft)', fontSize: 12, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {circle.description}
                            </div>
                          )}
                        </div>
                        <ChevronRight style={{ width: 18, height: 18, color: 'var(--lp-ink-mute)' }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {viewState === 'submitting' && (
            <div style={{ padding: '48px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <Loader2 style={{ width: 36, height: 36, color: 'var(--lp-pink)', animation: 'lp-spin 1s linear infinite' }} />
              <h2 className="lp-section-heading" style={{ fontSize: 22 }}>Setting up your interview…</h2>
              <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center' }}>
                Preparing your news tip submission for {selectedCircle?.name || 'the circle'}.
              </p>
            </div>
          )}

          {viewState === 'list' && (
            <>
              <div style={{ padding: '22px 24px 14px', borderBottom: '1px solid var(--lp-line)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1 }} />
                  <h2 className="lp-section-heading" style={{ textAlign: 'center', fontSize: 'clamp(20px, 3vw, 26px)', flex: 1, margin: 0 }}>
                    Your active interviews
                  </h2>
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setShowLanguageDropdown(v => !v)}
                      className="lp-frost-chip"
                      style={{ cursor: 'pointer', padding: '6px 10px' }}
                      aria-label="Change language"
                    >
                      <Globe style={{ width: 12, height: 12 }} />
                      <span>{currentLanguage}</span>
                      <ChevronDown style={{ width: 12, height: 12, transition: 'transform 180ms ease', transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0)' }} />
                    </button>
                    {showLanguageDropdown && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setShowLanguageDropdown(false)} />
                        <div
                          className="lp-card lp-card-tight"
                          style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 20, minWidth: 160, maxHeight: 280, overflowY: 'auto', padding: 4 }}
                        >
                          {LANGUAGES.map(lang => {
                            const selected = currentLanguage === lang.code;
                            return (
                              <button
                                key={lang.code}
                                type="button"
                                onClick={() => handleLanguageChange(lang.code)}
                                style={{
                                  width: '100%', padding: '8px 10px', border: 0,
                                  borderRadius: '2px 10px 2px 10px',
                                  background: selected ? 'rgba(236,72,153,0.10)' : 'transparent',
                                  color: selected ? 'var(--lp-ink)' : 'var(--lp-ink-soft)',
                                  fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.06em',
                                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  cursor: 'pointer', textAlign: 'left',
                                }}
                              >
                                <span>{lang.label}</span>
                                <span style={{ fontSize: 10, color: 'var(--lp-ink-mute)' }}>{lang.code}</span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ padding: '20px 24px 18px', overflowY: 'auto' }}>
                {!loading && !error && (
                  <div style={{ marginBottom: 18 }}>
                    <button type="button" onClick={handleNewsTipClick} className="lp-btn lp-btn-primary" style={{ width: '100%' }}>
                      <Lightbulb style={{ width: 16, height: 16 }} />
                      <span>Submit tip or video</span>
                    </button>
                  </div>
                )}

                {loading && interviews.length === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 14 }}>
                    <div className="lp-spinner" />
                    <p style={{ color: 'var(--lp-ink-mute)', fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.10em' }}>
                      Loading your interviews…
                    </p>
                  </div>
                )}

                {error && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 10 }}>
                    <Users style={{ width: 36, height: 36, color: 'var(--lp-ink-mute)' }} />
                    <p className="lp-serif" style={{ color: 'var(--lp-ink)', fontSize: 18 }}>Nothing here yet.</p>
                    <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', maxWidth: 360 }}>
                      You don't have any interviews at the moment.
                    </p>
                    <a href="https://www.proxiaistudio.com" target="_blank" rel="noopener noreferrer" className="lp-link" style={{ marginTop: 6, fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.10em' }}>
                      Visit Proxi Studio →
                    </a>
                  </div>
                )}

                {!loading && !error && interviews.length === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 10 }}>
                    <Users style={{ width: 36, height: 36, color: 'var(--lp-ink-mute)' }} />
                    <p className="lp-serif" style={{ color: 'var(--lp-ink)', fontSize: 18 }}>Nothing here yet.</p>
                    <p className="lp-prose" style={{ fontSize: 13, textAlign: 'center', maxWidth: 360 }}>
                      You'll see your pending interviews here when someone invites you to contribute.
                    </p>
                    <a href="https://www.proxiaistudio.com" target="_blank" rel="noopener noreferrer" className="lp-link" style={{ marginTop: 6, fontFamily: 'var(--lp-mono)', fontSize: 12, letterSpacing: '0.10em' }}>
                      Visit Proxi Studio →
                    </a>
                  </div>
                )}

                {!loading && !error && newInterviews.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                    <h3 className="lp-section-sub" style={{ paddingLeft: 4 }}>New ({newInterviews.length})</h3>
                    {newInterviews.map(renderInterviewRow)}
                  </div>
                )}

                {!loading && !error && inProgressInterviews.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setShowInProgress(v => !v)}
                      className="lp-btn-link"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 4px' }}
                    >
                      <span>In progress ({inProgressInterviews.length})</span>
                      <ChevronDown style={{ width: 14, height: 14, transition: 'transform 180ms ease', transform: showInProgress ? 'rotate(180deg)' : 'rotate(0)' }} />
                    </button>
                    {showInProgress && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {inProgressInterviews.map(renderInterviewRow)}
                      </div>
                    )}
                  </div>
                )}
              </div>

            </>
          )}
        </div>
      </main>

      <footer
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
        role="contentinfo"
      >
        <span className="lp-footer-link">
          Powered by{' '}
          <a href="https://www.ProxiAIStudio.com" target="_blank" rel="noopener noreferrer">ProxiAIStudio.com</a>
        </span>
      </footer>
    </LightShell>
  );
};

export default InterviewsListScreen;
